import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import { adminApi, apiError, MONTHS, STAGE_LABELS } from "../adminApi";
import { Panel, ScoreBar, Stat, buttonClass, inputClass } from "../components/Bits";

const WINDOWS = [
  { value: "this_month", label: "This month" },
  { value: "next_month", label: "Next month" },
  { value: "both", label: "Both" },
];

export default function AdminRenewals() {
  const [window_, setWindow] = useState("both");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState({ body: "", outcome: "" });

  const load = useCallback(async () => {
    try {
      const { data: d } = await adminApi.get("/crm/queue/renewals", {
        params: { window: window_, limit: 150 },
      });
      setData(d);
      setError("");
    } catch (e) {
      setError(apiError(e));
    }
  }, [window_]);

  useEffect(() => { load(); }, [load]);

  const logCall = async (accountId) => {
    if (!note.body.trim()) return;
    try {
      await adminApi.post("/crm/activities", {
        account_id: accountId, type: "call", body: note.body, outcome: note.outcome || undefined,
      });
      await adminApi.patch(`/crm/accounts/${accountId}`, { stage: "contacted" });
      setNote({ body: "", outcome: "" });
      setOpenId(null);
      toast.success("Call logged — moved to Contacted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (error) return <p className="text-sm text-terra" data-testid="renewals-error">{error}</p>;
  if (!data) return <p className="text-sm text-cream/40" data-testid="renewals-loading">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="admin-renewals-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Renewal radar</p>
          <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
            Today&rsquo;s call queue
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-cream/50">
            Employers whose plan year renews{" "}
            {data.months.map((m) => MONTHS[m - 1]).join(" or ")}, highest fit first. Do-not-contact
            accounts and closed deals are filtered out.
          </p>
        </div>
        <div className="flex gap-1 rounded-full border border-cream/15 p-1">
          {WINDOWS.map((option) => (
            <button key={option.value} type="button" onClick={() => setWindow(option.value)}
                    data-testid={`renewals-window-${option.value}`}
                    className={`rounded-full px-4 py-1.5 text-xs transition-colors duration-200 ${
                      window_ === option.value ? "bg-cream text-forest" : "text-cream/55 hover:text-cream"
                    }`}>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="In the queue" value={data.total.toLocaleString()} testid="renewals-total" />
        <Stat label="Never contacted" value={data.untouched.toLocaleString()}
              hint="No call, email or meeting logged" testid="renewals-untouched" />
        <Stat label="Showing" value={data.accounts.length} hint="Highest fit first" />
      </div>

      <Panel title="Queue" testid="panel-renewal-queue">
        <ul className="divide-y divide-cream/10">
          {data.accounts.map((account) => (
            <li key={account.id} className="py-4" data-testid={`renewal-row-${account.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <Link to={`/admin/accounts/${account.id}`}
                        className="text-sm text-cream transition-colors duration-200 hover:text-gold">
                    {account.name}
                  </Link>
                  <p className="mt-1 text-xs text-cream/45">
                    {[account.city, `${account.participants} lives`,
                      account.renewal_month ? `renews ${MONTHS[account.renewal_month - 1]}` : null,
                      STAGE_LABELS[account.stage] || "New"].filter(Boolean).join(" · ")}
                  </p>
                  <p className="mt-1 text-xs text-cream/60">
                    {account.phone ? (
                      <span className="inline-flex items-center gap-1.5 text-gold">
                        <Phone className="h-3 w-3" /> {account.phone}
                      </span>
                    ) : (
                      <span className="text-cream/30">No sponsor phone on file</span>
                    )}
                    {account.incumbent_broker ? (
                      <span className="ml-3 text-cream/45">Broker: {account.incumbent_broker}</span>
                    ) : null}
                    {account.peo_status === "likely" || account.current_peo ? (
                      <span className="ml-3 text-sage">
                        {account.current_peo ? `PEO: ${account.current_peo}` : "Likely on a PEO"}
                      </span>
                    ) : null}
                  </p>
                  <p className="mt-1 text-[11px] text-cream/35">
                    {account.last_touch
                      ? `Last ${account.last_touch.type} ${new Date(account.last_touch.created_at).toLocaleDateString()}`
                      : "Never contacted"}
                  </p>
                </div>

                <div className="flex flex-col items-end gap-2">
                  <ScoreBar score={account.score || 0} />
                  <button type="button"
                          onClick={() => setOpenId(openId === account.id ? null : account.id)}
                          data-testid={`renewal-log-toggle-${account.id}`}
                          className="text-xs text-cream/50 transition-colors duration-200 hover:text-gold">
                    {openId === account.id ? "Cancel" : "Log a call"}
                  </button>
                </div>
              </div>

              {openId === account.id ? (
                <div className="mt-4 space-y-2" data-testid={`renewal-log-form-${account.id}`}>
                  <textarea rows={2} value={note.body} placeholder="What happened on the call?"
                            onChange={(e) => setNote({ ...note, body: e.target.value })}
                            className={`${inputClass} resize-none`}
                            data-testid={`renewal-note-input-${account.id}`} />
                  <div className="flex flex-wrap gap-2">
                    <input value={note.outcome} placeholder="Outcome"
                           onChange={(e) => setNote({ ...note, outcome: e.target.value })}
                           className={`${inputClass} max-w-[14rem]`}
                           data-testid={`renewal-outcome-input-${account.id}`} />
                    <button type="button" onClick={() => logCall(account.id)} className={buttonClass}
                            data-testid={`renewal-log-submit-${account.id}`}>
                      Log call
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
          {data.accounts.length === 0 ? (
            <li className="py-4 text-sm text-cream/40" data-testid="renewals-empty">
              Nothing renewing in this window.
            </li>
          ) : null}
        </ul>
      </Panel>
    </div>
  );
}
